import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import reportService from '../../services/reportService';
import { productService } from '../../services/productService';
import { getWarehouse } from '../../services/warehousesService';
import entryGuideService from '../../services/entryGuideService';
import { formatDateLocaleLima } from '../../utils/dateUtils';
import { foldKardexMovements, kardexExitUnitPrice, kardexLineTotal, kardexSalidaTotalsWhenZeroingStock, kardexSalidaUnitPrice } from '../../utils/kardexMath';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';

const KardexRecalculate = () => {
  const { productId, warehouseId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const fromMonth = parseInt(searchParams.get('from_month')) || null;
  const fromYear = parseInt(searchParams.get('from_year')) || null;

  const [product, setProduct] = useState(null);
  const [warehouse, setWarehouse] = useState(null);
  const [kardexMovements, setKardexMovements] = useState([]);
  const [previousSummary, setPreviousSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingMovements, setPendingMovements] = useState([]);
  const [planWarning, setPlanWarning] = useState('');

  const hasLoadedData = useRef(false);
  const lastKardexParams = useRef(null);

  const loadProductAndWarehouse = async () => {
    try {
      const [productResponse, warehouseResponse] = await Promise.all([
        productService.getProduct(productId),
        getWarehouse(warehouseId)
      ]);

      if (productResponse.success) {
        setProduct(productResponse.data);
      }

      if (warehouseResponse.success) {
        setWarehouse(warehouseResponse.data);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
    }
  };

  const loadKardexDetail = useCallback(async () => {
    try {
      setLoading(true);
      // Cargar desde el mes especificado hasta el último movimiento (solo en recálculo)
      const kardexResponse = await reportService.getKardexDetail(
        productId, 
        warehouseId, 
        fromYear, 
        fromMonth, 
        true,   // fromMonth = true
        false,  // allMovements = false
        true    // recalculate = true → desde ese mes hasta el último movimiento
      );

      if (kardexResponse.success) {
        setKardexMovements(kardexResponse.data || []);
        setPreviousSummary(kardexResponse.previous_summary || null);
        
        // Obtener producto y almacén de los movimientos si están disponibles
        if (kardexResponse.data && kardexResponse.data.length > 0) {
          const firstMovement = kardexResponse.data[0];
          if (firstMovement.product) {
            setProduct(firstMovement.product);
          }
          if (firstMovement.warehouse) {
            setWarehouse(firstMovement.warehouse);
          }
        }
      }
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar el detalle de kardex',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [productId, warehouseId, fromYear, fromMonth]);

  useEffect(() => {
    if (!hasLoadedData.current && productId && warehouseId) {
      hasLoadedData.current = true;
      loadProductAndWarehouse();
    }
  }, [productId, warehouseId]);

  useEffect(() => {
    if (productId && warehouseId && fromMonth && fromYear) {
      // Crear una clave única para los parámetros de kardex
      const kardexKey = `${productId}-${warehouseId}-${fromYear}-${fromMonth}`;
      
      // Solo cargar si los parámetros han cambiado (evita doble llamada en StrictMode)
      if (lastKardexParams.current !== kardexKey) {
        lastKardexParams.current = kardexKey;
        loadKardexDetail();
      }
    }
  }, [productId, warehouseId, fromMonth, fromYear, loadKardexDetail]);

  const initialStock = Number(previousSummary?.stock) || 0;
  const initialTotalValue = Number(previousSummary?.total_value) || 0;

  // Recalcular salidas con q3/q4 (misma lógica que backend) para montos exactos a 4 decimales.
  const devolucionRefByGuideCode = {};
  const movementsWithStock = foldKardexMovements(
    kardexMovements,
    initialStock,
    initialTotalValue,
    (movement, { averageCostBefore, stockBefore, totalValueBefore, lastAverageCost }) => {
      let adjustedUnitPrice = movement.unit_price;
      let adjustedTotal = movement.total;
      let usedDevolucionRef = false;

      if (
        movement.movement_type === 'entrada' &&
        movement.kardex_event === 'recepcion' &&
        movement.cost_mismatch_alert?.has_mismatch
      ) {
        adjustedUnitPrice = movement.cost_mismatch_alert.transfer_unit_price;
        adjustedTotal = kardexLineTotal(movement.quantity, adjustedUnitPrice);
      }

      if (
        movement.movement_type === 'entrada' &&
        movement.kardex_event === 'devolucion' &&
        movement.consumption_exit_guide_code
      ) {
        const refUnit = devolucionRefByGuideCode[movement.consumption_exit_guide_code];
        if (refUnit != null) {
          adjustedUnitPrice = refUnit;
          adjustedTotal = kardexLineTotal(movement.quantity, adjustedUnitPrice);
          usedDevolucionRef = true;
        } else if (movement.reference_guide_unit_price != null) {
          adjustedUnitPrice = movement.reference_guide_unit_price;
          adjustedTotal = kardexLineTotal(movement.quantity, adjustedUnitPrice);
          usedDevolucionRef = true;
        }
      }

      if (
        movement.movement_type === 'entrada' &&
        movement.kardex_event === 'ajuste_inventario' &&
        (stockBefore > 0 || lastAverageCost > 0)
      ) {
        const basis = stockBefore > 0 ? averageCostBefore : lastAverageCost;
        adjustedUnitPrice = kardexExitUnitPrice(basis);
        adjustedTotal = kardexLineTotal(movement.quantity, adjustedUnitPrice);
      }

      if (movement.movement_type === 'salida') {
        const zeroStockAdj = kardexSalidaTotalsWhenZeroingStock(
          stockBefore,
          totalValueBefore,
          movement.quantity,
          averageCostBefore,
          lastAverageCost
        );
        if (zeroStockAdj) {
          adjustedUnitPrice = zeroStockAdj.unit_price;
          adjustedTotal = zeroStockAdj.total;
        } else {
          const exitUnit = kardexSalidaUnitPrice(averageCostBefore, lastAverageCost);
          if (exitUnit > 0) {
            adjustedUnitPrice = exitUnit;
            adjustedTotal = kardexLineTotal(movement.quantity, adjustedUnitPrice);
          }
        }
      }

      const shouldAdjust =
        movement.movement_type === 'salida' ||
        (movement.movement_type === 'entrada' &&
          movement.kardex_event === 'ajuste_inventario') ||
        (movement.movement_type === 'entrada' &&
          movement.kardex_event === 'recepcion' &&
          movement.cost_mismatch_alert?.has_mismatch) ||
        usedDevolucionRef;

      if (shouldAdjust) {
        if (movement.entry_guide_code) {
          devolucionRefByGuideCode[movement.entry_guide_code] = adjustedUnitPrice;
        }
        return { unit_price: adjustedUnitPrice, total: adjustedTotal };
      }

      if (movement.entry_guide_code) {
        devolucionRefByGuideCode[movement.entry_guide_code] = movement.unit_price;
      }
      return null;
    }
  ).map((movement, index) => {
    const original = kardexMovements[index];
    return {
      ...movement,
      stockAfter: movement.stock_after,
      totalValueAfter: movement.total_value_after,
      averageCostAfter: movement.average_cost_after,
      original_unit_price: original?.unit_price,
      original_total: original?.total,
      cost_mismatch_alert: original?.cost_mismatch_alert?.has_mismatch ? original.cost_mismatch_alert : null,
    };
  });

  // Función para preparar y mostrar modal de confirmación
  const handleRecalculate = async () => {
    try {
      setRecalculating(true);

      const planResponse = await reportService.getKardexRecalculatePlan(productId, warehouseId);
      const movementsToUpdate = planResponse.data || [];

      if (movementsToUpdate.length === 0) {
        setToast({
          isOpen: true,
          message: 'No hay movimientos que se puedan recalcular. Los costos en guías y kardex ya están alineados, o solo hay movimientos sin guía asociada.',
          type: 'info'
        });
        return;
      }

      setPendingMovements(movementsToUpdate);
      setPlanWarning(planResponse.warning || '');
      setIsConfirmModalOpen(true);
    } catch (error) {
      console.error('Error al preparar recálculo:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al preparar el recálculo',
        type: 'error'
      });
    } finally {
      setRecalculating(false);
    }
  };

  // Función para ejecutar el recálculo después de confirmar
  const executeRecalculate = async () => {
    try {
      setRecalculating(true);

      // Llamar al API para recalcular
      const response = await entryGuideService.recalculateGuides(pendingMovements);

      if (response.success) {
        const { updated_guides: guides = 0, updated_items: items = 0, updated_movements: movements = 0 } = response.data || {};
        const allZero = guides === 0 && items === 0 && movements === 0;

        setToast({
          isOpen: true,
          message: allZero
            ? 'No se aplicaron cambios: los valores en guía ya coincidían con el recálculo.'
            : `Recálculo exitoso: ${guides} guía(s), ${items} ítem(s) y ${movements} movimiento(s) de kardex actualizado(s)`,
          type: allZero ? 'info' : 'success'
        });

        // Recargar los datos después de un breve delay
        setTimeout(() => {
          loadKardexDetail();
        }, 1000);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al recalcular las guías',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al recalcular:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al recalcular las guías',
        type: 'error'
      });
    } finally {
      setRecalculating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Recálculo de Kardex</h1>
            {product && warehouse && (
              <p className="text-gray-600">
                Producto: <span className="font-medium">{product.name}</span> | 
                Almacén: <span className="font-medium">{warehouse.code} - {warehouse.name}</span>
              </p>
            )}
            {fromMonth && fromYear && (
              <p className="text-sm text-gray-500 mt-2">
                Movimientos desde {new Date(fromYear, fromMonth - 1).toLocaleDateString('es-PE', { month: 'long', year: 'numeric' })} hasta la fecha
              </p>
            )}
          </div>
          <button
            onClick={handleRecalculate}
            disabled={recalculating}
            className="flex items-center gap-2 px-6 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors"
          >
            {recalculating ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Recalculando...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Recalcular Guías
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th rowSpan="2" className="px-2 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border border-gray-300">
                  FECHA
                </th>
                <th rowSpan="2" className="px-2 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border border-gray-300">
                  HORA
                </th>
                <th rowSpan="2" className="px-2 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border border-gray-300">
                  EVENTO
                </th>
                <th rowSpan="2" className="px-2 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border border-gray-300">
                  GUÍA
                </th>
                <th rowSpan="2" className="px-2 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border border-gray-300">
                  ENTIDAD
                </th>
                <th rowSpan="2" className="px-2 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border border-gray-300">
                  REFERENCIA
                </th>
                <th colSpan="3" className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border border-gray-300 bg-green-100">
                  ENTRADAS
                </th>
                <th colSpan="3" className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border border-gray-300 bg-red-100">
                  SALIDAS
                </th>
                <th colSpan="3" className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border border-gray-300 bg-blue-100">
                  EXISTENCIAS
                </th>
                <th rowSpan="2" className="px-2 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border border-gray-300">
                  CONDICIÓN
                </th>
              </tr>
              <tr>
                <th className="px-1 py-2 text-xs font-semibold text-gray-700 uppercase border border-gray-300 bg-green-100">UNIDADES</th>
                <th className="px-1 py-2 text-xs font-semibold text-gray-700 uppercase border border-gray-300 bg-green-100">COSTO UNIT</th>
                <th className="px-1 py-2 text-xs font-semibold text-gray-700 uppercase border border-gray-300 bg-green-100">VALOR TOTAL</th>
                <th className="px-1 py-2 text-xs font-semibold text-gray-700 uppercase border border-gray-300 bg-red-100">UNIDADES</th>
                <th className="px-1 py-2 text-xs font-semibold text-gray-700 uppercase border border-gray-300 bg-red-100">COSTO UNIT</th>
                <th className="px-1 py-2 text-xs font-semibold text-gray-700 uppercase border border-gray-300 bg-red-100">VALOR TOTAL</th>
                <th className="px-1 py-2 text-xs font-semibold text-gray-700 uppercase border border-gray-300 bg-blue-100">UNIDADES</th>
                <th className="px-1 py-2 text-xs font-semibold text-gray-700 uppercase border border-gray-300 bg-blue-100">COSTO UNIT</th>
                <th className="px-1 py-2 text-xs font-semibold text-gray-700 uppercase border border-gray-300 bg-blue-100">VALOR TOTAL</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Fila de movimientos anteriores (saldo anterior) */}
              {previousSummary && previousSummary.stock !== 0 && previousSummary.stock > 0 && (
                <tr className="bg-blue-50">
                  <td colSpan="6" className="px-2 py-2 text-xs font-bold text-gray-900 border border-gray-300">
                    MOVIMIENTOS ANTERIORES (RESUMEN)
                  </td>
                  <td className="px-1 py-2 text-xs text-gray-900 text-right border border-gray-300 bg-green-50">-</td>
                  <td className="px-1 py-2 text-xs text-gray-900 text-right border border-gray-300 bg-green-50">-</td>
                  <td className="px-1 py-2 text-xs text-gray-900 text-right border border-gray-300 bg-green-50">-</td>
                  <td className="px-1 py-2 text-xs text-gray-900 text-right border border-gray-300 bg-red-50">-</td>
                  <td className="px-1 py-2 text-xs text-gray-900 text-right border border-gray-300 bg-red-50">-</td>
                  <td className="px-1 py-2 text-xs text-gray-900 text-right border border-gray-300 bg-red-50">-</td>
                  <td className="px-1 py-2 text-xs text-gray-900 text-right border border-gray-300 bg-blue-50 font-medium">
                    {previousSummary.stock.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                  </td>
                  <td className="px-1 py-2 text-xs text-gray-900 text-right border border-gray-300 bg-blue-50 font-medium">
                    {previousSummary.average_cost !== 0 ? `S/ ${previousSummary.average_cost.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}` : '-'}
                  </td>
                  <td className="px-1 py-2 text-xs text-gray-900 text-right border border-gray-300 bg-blue-50 font-medium">
                    {`S/ ${previousSummary.total_value.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900 border border-gray-300">
                    {previousSummary.stock_by_condition ? (
                      <div className="text-xs">
                        Bueno: {previousSummary.stock_by_condition.good || 0}
                        {previousSummary.stock_by_condition.damaged > 0 && `, Dañado: ${previousSummary.stock_by_condition.damaged}`}
                        {previousSummary.stock_by_condition.defective > 0 && `, Defectuoso: ${previousSummary.stock_by_condition.defective}`}
                      </div>
                    ) : '-'}
                  </td>
                </tr>
              )}
              
              {movementsWithStock.length === 0 && !previousSummary ? (
                <tr>
                  <td colSpan="18" className="px-4 py-8 text-center text-gray-500 border border-gray-300">
                    No hay movimientos registrados
                  </td>
                </tr>
              ) : (
                movementsWithStock.map((movement) => (
                  <tr key={movement.id} className="hover:bg-gray-50">
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 border border-gray-300">
                      {formatDateLocaleLima(movement.movement_date)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 border border-gray-300">
                      {movement.movement_time || '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 border border-gray-300">
                      {movement.kardex_event === 'compra' ? 'Compra' :
                       movement.kardex_event === 'venta' ? 'Venta' :
                       movement.kardex_event === 'saldo_inicial' ? 'Saldo Inicial' :
                       movement.kardex_event === 'traspaso' ? 'Traspaso' :
                       movement.kardex_event === 'recepcion' ? 'Recepción' :
                       movement.kardex_event === 'ajuste_inventario' ? 'Ajuste inventario' :
                       movement.kardex_event === 'transferencia_gratuita' ? 'Transferencia Gratuita' :
                       movement.kardex_event === 'descuento_obtenido' ? 'Descuento Obtenido' :
                       movement.kardex_event === 'descuento_concedido' ? 'Descuento Concedido' :
                       movement.kardex_event === 'descuento_con_mercaderia' ? 'Descuento con Mercadería' :
                       movement.kardex_event === 'consumo' ? 'Consumo' :
                       movement.kardex_event === 'devolucion' ? 'Devolución' :
                       movement.kardex_event}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 border border-gray-300">
                      {movement.entry_guide_code || '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 border border-gray-300">
                      {movement.supplier_name || '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 border border-gray-300">
                      {movement.kardex_event === 'devolucion' && movement.consumption_exit_guide_code ? (
                        movement.consumption_exit_guide_code
                      ) : movement.document ? (
                        <div>
                          {movement.document.series && movement.document.number ? (
                            <div>
                              <div className="font-medium">{movement.document.series}-{movement.document.number}</div>
                              <div className="text-xs text-gray-400">{movement.document.code}</div>
                            </div>
                          ) : (
                            <div>{movement.document.code}</div>
                          )}
                        </div>
                      ) : movement.purchase_order ? (
                        <div>
                          <div className="font-medium">{movement.purchase_order.code}</div>
                          <div className="text-xs text-gray-400">Orden de Compra</div>
                        </div>
                      ) : '-'}
                    </td>
                    {/* ENTRADAS */}
                    <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-green-50">
                      {movement.movement_type === 'entrada' && movement.kardex_event !== 'devolucion' ? (
                        <>
                          {movement.quantity.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                          {movement.original_quantity !== movement.quantity && (
                            <div className="text-xs text-gray-400">
                              ({movement.original_quantity.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {movement.original_unit_of_measure})
                            </div>
                          )}
                        </>
                      ) : '-'}
                    </td>
                    <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-green-50">
                      {movement.movement_type === 'entrada' && movement.kardex_event !== 'devolucion' ? `S/ ${movement.unit_price.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}` : '-'}
                    </td>
                    <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-green-50">
                      {movement.movement_type === 'entrada' && movement.kardex_event !== 'devolucion' ? `S/ ${movement.total.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}` : '-'}
                    </td>
                    {/* SALIDAS */}
                    <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-red-50">
                      {movement.movement_type === 'salida' ? (
                        <>
                          {movement.quantity.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                          {movement.original_quantity !== movement.quantity && (
                            <div className="text-xs text-gray-400">
                              ({movement.original_quantity.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {movement.original_unit_of_measure})
                            </div>
                          )}
                        </>
                      ) : movement.movement_type === 'entrada' && movement.kardex_event === 'devolucion' ? (
                        <>
                          {(-movement.quantity).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                          {movement.original_quantity !== movement.quantity && (
                            <div className="text-xs text-gray-400">
                              ({movement.original_quantity.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {movement.original_unit_of_measure})
                            </div>
                          )}
                        </>
                      ) : '-'}
                    </td>
                    <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-red-50">
                      {movement.movement_type === 'salida' ? `S/ ${movement.unit_price.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}` : movement.movement_type === 'entrada' && movement.kardex_event === 'devolucion' ? `S/ ${movement.unit_price.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}` : '-'}
                    </td>
                    <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-red-50">
                      {movement.movement_type === 'salida' ? `S/ ${movement.total.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}` : movement.movement_type === 'entrada' && movement.kardex_event === 'devolucion' ? `S/ ${(-movement.total).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}` : '-'}
                    </td>
                    {/* EXISTENCIAS */}
                    <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-blue-50">
                      {movement.stockAfter.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                    </td>
                    <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-blue-50">
                      {movement.averageCostAfter !== 0 ? `S/ ${movement.averageCostAfter.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}` : '-'}
                    </td>
                    <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 text-right border border-gray-300 bg-blue-50">
                      {typeof movement.totalValueAfter === 'number' && !Number.isNaN(movement.totalValueAfter)
                        ? `S/ ${Number(movement.totalValueAfter).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`
                        : '-'}
                    </td>
                    {/* CONDICIÓN */}
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 border border-gray-300">
                      {movement.condition === 'good' ? 'Bueno' :
                       movement.condition === 'damaged' ? 'Dañado' :
                       movement.condition === 'defective' ? 'Defectuoso' :
                       movement.condition}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={executeRecalculate}
        title="Confirmar Recálculo"
        message={`¿Está seguro de recalcular ${pendingMovements.length} movimiento(s)? Esto actualizará los costos y valores totales en las guías correspondientes.${planWarning ? `\n\n${planWarning}` : ''}`}
        confirmText="Aceptar"
        cancelText="Cancelar"
      />
    </div>
  );
};

export default KardexRecalculate;

